import http from './http';

export interface Plan {
  plan_key: string;
  display_name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  billing_interval: string;
  module_key: string | null;
  sort_order: number;
  stripe_price_id: string | null;
}

export interface PlansResponse {
  plans: Plan[];
  stripe_active: boolean;
}

export interface Subscription {
  id: string;
  plan_key: string;
  plan_name: string;
  module_key: string | null;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  started_at: string | null;
  canceled_at: string | null;
  stripe_subscription_id: string | null;
}

export interface PaymentMethodRef {
  id: string;
  user_id: string;
  provider: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
  created_at: string | null;
}

export interface BillingHistory {
  payments: Array<{
    id: string;
    event_type: string;
    amount_cents: number | null;
    currency: string | null;
    status: string | null;
    stripe_subscription_id: string | null;
    created_at: string | null;
  }>;
  subscriptions: Subscription[];
  stripe_active: boolean;
}

export interface SubscriptionsResponse {
  subscriptions: Subscription[];
}

export interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
  plan_key: string;
  plan_name: string;
  price_cents: number;
}

export interface StripeConfigResponse {
  configured: boolean;
  available: boolean;
  is_owner: boolean;
  mode: string | null;
  publishable_key: string | null;
  has_secret_key: boolean;
  has_webhook_secret: boolean;
  is_active: boolean;
  updated_at: string | null;
}

export interface PortalStats {
  total_users: number;
  total_subscriptions: number;
  active_subscriptions: number;
  revenue_cents: number;
  mrr_cents: number;
  revenue_by_plan: Record<string, number>;
  plan_counts: Record<string, number>;
  subscriptions_by_status: Record<string, number>;
  outstanding_cents: number;
  overdue_invoices: number;
  open_invoices: number;
  failed_payments: number;
  churned_30d: number;
  recent_orders: Array<{
    id: string;
    user_name: string;
    user_email: string | null;
    plan_key: string | null;
    total_cents: number;
    currency: string;
    status: string;
    created_at: string | null;
  }>;
  recent_signups: Array<{ id: string; name: string; email: string; created_at: string }>;
  recent_subscriptions: Array<{
    id: string;
    user_name: string;
    user_email: string;
    plan_name: string;
    status: string;
    started_at: string | null;
    current_period_end: string | null;
  }>;
}

export interface BillingAddon {
  addon_key: string;
  name: string;
  description: string | null;
  category: string;
  unit_label: string | null;
  price_cents: number;
  currency: string;
  billing_interval: string | null;
  max_quantity: number | null;
  provider: string | null;
  is_self_service: boolean;
  is_active: boolean;
  sort_order: number;
  margin_cents?: number | null;
  provider_cost_cents?: number | null;
}

export interface AddonCatalog {
  addons: BillingAddon[];
  categories: Array<{ key: string; count: number }>;
  by_category: Record<string, BillingAddon[]>;
}

export interface BillingOrderItem {
  addon_key: string;
  name: string;
  category: string;
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  billing_interval: string | null;
  provider: string | null;
  provider_product_key: string | null;
  domain_name: string | null;
}

export interface BillingOrder {
  id: string;
  plan_key: string | null;
  billing_interval: string | null;
  items: BillingOrderItem[];
  subtotal_cents: number;
  total_cents: number;
  currency: string;
  status: string;
  provider_ref: string | null;
  created_at: string | null;
}

export interface PricingRow {
  kind: 'plan' | 'addon';
  key: string;
  name: string;
  category?: string;
  price_cents: number;
  effective_price_cents: number | null;
  currency: string;
  billing_interval: string | null;
  is_active: boolean;
  pricing_mode: 'fixed' | 'markup';
  markup_pct: number | null;
  provider?: string | null;
  provider_product_key?: string | null;
  provider_cost_cents: number | null;
  margin_cents: number | null;
  below_cost: boolean;
  cost_updated_at: string | null;
}

export interface PricingData {
  plans: PricingRow[];
  addons: PricingRow[];
  default_markup_pct: number;
  below_cost: string[];
}

export interface CartLine {
  id: string;
  addon_key: string;
  name: string;
  category: string;
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  billing_interval: string | null;
  provider: string | null;
  provider_product_key: string | null;
  domain_name: string | null;
  plan_key: string | null;
  is_active: boolean;
}

export interface ProductTypeOption {
  key: string;
  tld?: string;
  label: string;
  price_cents: number;
  interval: string | null;
  provider?: string;
  provider_product_key?: string;
  module_key?: string | null;
}

export interface ProductType {
  key: string;
  label: string;
  group: 'core' | 'additional';
  blurb: string;
  options: ProductTypeOption[];
}

export interface CartData {
  cart_id: string;
  cart_token: string | null;
  user_id: string | null;
  status: string;
  items: CartLine[];
  item_count: number;
  line_count: number;
  subtotal_cents: number;
  currency: string;
  note: string | null;
}

export const billingApi = {
  getPlans: () => http.get<PlansResponse>('/billing/plans').then(r => r.data),
  getSubscriptions: () => http.get<SubscriptionsResponse>('/billing/subscriptions').then(r => r.data),
  checkout: (plan_key: string, success_url: string, cancel_url: string) =>
    http.post<CheckoutResponse>('/billing/checkout', { plan_key, success_url, cancel_url }).then(r => r.data),
  openPortal: (return_url: string) =>
    http.post<{ portal_url: string }>('/billing/portal', { return_url }).then(r => r.data),
  getStripeConfig: () => http.get<StripeConfigResponse>('/billing/stripe-config').then(r => r.data),
  saveStripeConfig: (data: { publishable_key: string; secret_key: string; webhook_secret?: string; mode: string }) =>
    http.put<StripeConfigResponse>('/billing/stripe-config', data).then(r => r.data),
  updatePlanPrice: (plan_key: string, price_cents: number) =>
    http.put('/billing/plans/price', { plan_key, price_cents }).then(r => r.data),
  updatePlanStripeId: (plan_key: string, stripe_price_id: string) =>
    http.put('/billing/plans/stripe-id', { plan_key, stripe_price_id }).then(r => r.data),
  getPortalStats: () => http.get<PortalStats>('/billing/portal/stats').then(r => r.data),
  getPaymentMethods: (refresh = true) =>
    http.get<{ payment_methods: PaymentMethodRef[]; note: string }>('/billing/payment-methods', { params: { refresh } }).then(r => r.data),
  startCardSetup: (success_url: string, cancel_url: string) =>
    http.post<{ checkout_url: string; session_id: string }>('/billing/payment-methods/setup', { success_url, cancel_url }).then(r => r.data),
  getHistory: () => http.get<BillingHistory>('/billing/history').then(r => r.data),

  // Pricing control (owner)
  getPricing: () => http.get<PricingData>('/billing/pricing').then(r => r.data),
  setPricing: (data: { kind: 'plan' | 'addon'; key: string; price_cents?: number; pricing_mode?: 'fixed' | 'markup'; markup_pct?: number; provider_cost_cents?: number }) =>
    http.put<PricingRow>('/billing/pricing', data).then(r => r.data),
  refreshCosts: () => http.post<{ updated: Array<{ key: string; old_cents: number | null; new_cents: number }>; unchanged_count: number; missing_mapping: string[]; below_cost: string[]; cost_source: string }>('/billing/pricing/refresh-costs').then(r => r.data),
  suggestPrices: (items: Array<{ kind: string; key: string; markup_pct?: number }>) =>
    http.post<{ suggestions: Array<{ kind: string; key: string; name?: string; ok: boolean; current_price_cents?: number | null; suggested_price_cents?: number; basis?: string; markup_pct?: number; provider_cost_cents?: number | null; source?: string; reason?: string }> }>('/billing/pricing/suggest', { items }).then(r => r.data),

  // Cart (multi-item, checkout screens; guest carts merge on sign-in)
  getCart: () => http.get<CartData>('/billing/cart').then(r => r.data),
  addToCart: (data: { addon_key: string; quantity?: number; domain_name?: string | null; plan_key?: string | null }) =>
    http.post<CartData>('/billing/cart/items', data).then(r => r.data),
  updateCartItem: (id: string, data: { quantity?: number; domain_name?: string | null }) =>
    http.patch<CartData>(`/billing/cart/items/${id}`, data).then(r => r.data),
  removeCartItem: (id: string) => http.delete<CartData>(`/billing/cart/items/${id}`).then(r => r.data),
  clearCart: () => http.delete<CartData>('/billing/cart').then(r => r.data),
  mergeCart: (cart_token: string) => http.post<CartData>('/billing/cart/merge', { cart_token }).then(r => r.data),
  cartCheckout: () => http.post<{
    order_id: string; two_charges: boolean; customer_notice: string;
    stripe_url: string | null; rc_url: string | null; rc_status: string | null;
    stripe_total_cents: number; rc_total_cents: number;
    rc_next_step?: string; rc_error?: string; stripe_error?: string; order: BillingOrder;
  }>('/billing/cart/checkout', {}).then(r => r.data),

  // Multi-step billing: add-on catalog + orders
  getAddons: () => http.get<AddonCatalog>('/billing/addons').then(r => r.data),
  getAddonsAdmin: () => http.get<{ addons: BillingAddon[]; count: number }>('/billing/addons/admin').then(r => r.data),
  updateAddon: (addon_key: string, data: { price_cents?: number; is_active?: boolean; description?: string }) =>
    http.put<BillingAddon>(`/billing/addons/${addon_key}`, data).then(r => r.data),
  seedAddons: () => http.post<{ created: number; total: number }>('/billing/seed-addons').then(r => r.data),
  createOrder: (data: { plan_key?: string | null; interval?: string | null; items: Array<{ addon_key: string; quantity: number; domain_name?: string | null }> }) =>
    http.post<BillingOrder>('/billing/orders', data).then(r => r.data),
  getOrders: () => http.get<{ orders: BillingOrder[] }>('/billing/orders').then(r => r.data),
  getAllOrders: () => http.get<{ orders: Array<BillingOrder & { user_name?: string; user_email?: string | null }>; count: number }>('/billing/admin/orders').then(r => r.data),
  syncBillingCalendar: () => http.post<{ created: number; workspace_id?: string; reason?: string }>('/billing/portal/sync-calendar', {}).then(r => r.data),
  getBillingFeed: () => http.get<{ revenue_cents: number; rc_wholesale_cost_cents: number; plans: Array<{ plan_key: string; price_cents: number; interval: string }>; note: string }>('/billing/feed').then(r => r.data),
  getProductTypes: () => http.get<{ types: Array<{ key: string; label: string; group: 'core' | 'additional'; blurb: string; options: Array<{ key: string; tld?: string; label: string; price_cents: number; interval: string | null; provider?: string; provider_product_key?: string; module_key?: string | null }> }> }>('/billing/product-types').then(r => r.data),
  cancelOrder: (id: string) => http.post<BillingOrder>(`/billing/orders/${id}/cancel`).then(r => r.data),
};